<template>
  <jackpots
    :is-loading="isLoading"
    :jackpots="jackpots"
    :total="total"
    :games="games"
    :brands="brands"
    :currencies="currencies"
    :project-name-by-id="projectNameById"
    :game-name-by-id="gameNameById"
    :brand-name-by-id="brandNameById"
    :history="history"
    :save-success-flag="saveSuccessFlag"
    :audit-log="auditLog"
    :can-edit="canEditJackpots"
    :last-project="lastProject"
    @search="loadData"
    @save="onSave"
    @toggle="onToggle"
    @show-history="onShowHistory"
    @show-audit-log="onShowAuditLog"
    @re-issue="onReissuePrizepool"
  />
</template>

<script>
import { API } from '@/utils/api'
import Jackpots from '../components/Jackpots.vue'

const PRIZEPOOL_STATUS_GRANTED = 'granted'

export default {
  components: {
    Jackpots,
  },
  data() {
    return {
      isLoading: false,
      jackpots: [],
      total: 0,
      games: [],
      brands: [],
      currencies: this.$store.state.auth.currencies,
      lastProject: null,
      history: [],
      auditLog: [],

      // We need this Container's context to decide if we are ready to close the editing form
      saveSuccessFlag: false,
    }
  },
  computed: {
    projectNameById() {
      const projectNameById = {}
      for (const project of this.$store.state.auth.entities) {
        projectNameById[project.entity_id] = project.name
      }

      return projectNameById
    },
    gameNameById() {
      const gameNameById = {}
      for (const game of this.games) {
        gameNameById[game.game_id] = `${game.name} (${game.system_name} ${game.game_id})`
      }
      return gameNameById
    },
    brandNameById() {
      const brandNameById = {}
      for (const brand of this.brands) {
        brandNameById[brand.id] = brand.name
      }

      return brandNameById
    },
    canEditJackpots() {
      return this.$store.state.auth.checkAccess({ key: 'jackpot_edit', value: true })
    },
  },
  methods: {
    loadData(args) {
      const needsNewGameList = args.project !== this.lastProject

      this.isLoading = true
      this.lastProject = args.project
      return Promise.all([
        API.call('/$v/jackpot/list.json', { entity_id: args.project, ...args }),
        needsNewGameList ? API.call('/$v/game/list.json', {}, null, args.project) : Promise.resolve(),
      ])
        .then(([r1, r2]) => {
          this.jackpots = r1.data
          this.total = r1.total
          if (r2) {
            this.games = r2.data
            this.brands = r2.brands
          }
        })
        .finally(() => this.isLoading = false)
    },
    /**
     *
     * @param {Object} args
     * @param {Object} args.jackpot - jackpot to save
     * @param {Object} args.searchArgs - search params to reload the table with on success
     */
    onSave(args) {
      this.isLoading = true
      return API.call('/$v/jackpot/update.json', {
        ...args.jackpot,
        entity_id: args.searchArgs.project,
      })
        .then(() => this.saveSuccessFlag = !this.saveSuccessFlag)
        .then(() => this.$showSuccess('Jackpot saved successfully'))
        .then(r => this.loadData(args.searchArgs))
        .finally(() => this.isLoading = false)
    },
    /**
     *
     * @param {Object} args
     * @param {number[]} args.jackpot_ids - ids to toggle status for
     * @param {'upcoming' | 'active' | 'finished' | 'disabled'} args.status - ids to toggle status for
     * @param {'upcoming' | 'active' | 'finished' | 'disabled'} args.entity_id - project id from filter jackpot was loaded with
     */
    onToggle(args) {
      const isSingular = args.jackpot_ids.length === 1
      const msg = isSingular
        ? `this project's status to ${args.status}`
        : `${args.jackpot_ids.length} selected projects' status to ${args.status}`

      this.$root.$confirm('Confirm', `Are you sure you want to set ${msg}?`)
        .then(yes => {
          if (!yes) {
            return
          }

          this.isLoading = true
          API.call('/$v/jackpot/toggle.json', args)
            .then(r => {
              for (const jackpot of this.jackpots) {
                if (args.jackpot_ids.includes(jackpot.jackpot_id)) {
                  jackpot.status = args.status
                }
              }

              this.$showSuccess('Status updated successfully')
            })
            .finally(() => this.isLoading = false)
        })
    },
    onShowHistory(args) {
      this.history = []
      this.isLoading = true
      return API.call('/$v/jackpot/prizepool/list.json', args)
        .then(r => {
          this.history = r.data ?? []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onShowAuditLog(args) {
      this.auditLog = []
      this.isLoading = true
      return API.call('/$v/jackpot/audit_log.json', args)
        .then(r => {
          this.auditLog = r.data ?? []
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    onReissuePrizepool(args) {
      this.$root.$confirm('Confirm', `Re-issue this prizepool bonus?`)
        .then(yes => {
          if (!yes) {
            return
          }

          this.isLoading = true
          return API.call('/$v/jackpot/prizepool/regrant.json', {
            prizepool_id: args.prizepool_id,
            entity_id: this.lastProject,
          })
            .then(r => {

              if (r.data.success) {
                args.status = PRIZEPOOL_STATUS_GRANTED

                this.$showSuccess('Bonus re-issued successfully!')
              } else {
                this.$showError(r.data.error)
              }

            })
            .finally(() => {
              this.isLoading = false
            })
        })
    },
  }
}
</script>
